<template>
  <div
    ref="locationSearchRef"
    class="relative w-full"
  >
    <h4
      v-if="title"
      class="mb-1 mt-2 text-left"
    >
      {{ title }}
    </h4>
    <div class="relative">
      <input
        v-model="locationInput"
        type="text"
        class="w-full rounded-md border-steel-300 bg-lighter-gray bg-opacity-30 pr-3 text-sm text-steel-800 outline-none"
        :class="{
          'pl-10': showIcons,
          'h-9': isSlim,
          'h-12': !isSlim,
        }"
        :placeholder="placeholder"
        :tabindex="0"
        @input="autocompleteInput"
        @focus="isFocused = true"
      />

      <img
        v-if="showClearIcon && locationInput?.length"
        class="absolute right-6 top-[20px] h-2 w-2 cursor-pointer opacity-30"
        src="@/assets/close-icon.svg"
        alt="close icon"
        @click="handleClear"
      />

      <div v-if="showIcons">
        <img
          v-if="type === 'origin'"
          class="absolute left-3"
          :class="isSlim ? 'top-2' : 'top-3.5'"
          src="@/assets/origin-marker.svg"
          alt="origin icon"
        />
        <img
          v-else
          class="absolute left-2.5"
          :class="isSlim ? 'top-1.5' : 'top-3'"
          src="@/assets/loads/destination-marker.svg"
          alt="destination icon"
        />
      </div>
    </div>

    <ul
      v-if="isLocationResultsLoading"
      class="absolute z-50 w-full rounded-md bg-white py-2 shadow-lg"
      :class="isSlim ? 'top-[36px]' : 'top-[44px]'"
    >
      <img
        class="loading m-auto"
        src="@/assets/loading-v2-yellow.svg"
        alt="loading spinner"
      />
    </ul>

    <ul
      v-else-if="results?.length > 0 && isFocused"
      class="absolute z-50 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm"
      :class="isSlim ? 'top-[36px]' : 'top-[44px]'"
      tabindex="-1"
    >
      <li
        v-for="(item, itemIndex) in results"
        :key="itemIndex"
        class="group relative cursor-pointer select-none py-2 pl-8 pr-4 hover:bg-yellow-500 hover:text-white focus:bg-yellow-500 focus:text-white focus:outline-none"
        :tabindex="0"
        @click="handleAutocompleteResult(item)"
        @keydown.enter="handleAutocompleteResult(item)"
      >
        <span class="flex h-6 items-center truncate font-medium">
          {{ item.description }}
        </span>
      </li>
    </ul>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import debounce from 'lodash.debounce';
import { useStore } from 'vuex';
import { useClickAway } from '@/utils';

const props = defineProps({
  title: {
    type: String,
  },
  type: {
    type: String,
    required: true,
  },
  inputValue: {
    type: String,
    required: true,
  },
  placeholder: {
    type: String,
    default: 'Search',
  },
  showIcons: {
    type: Boolean,
    default: true,
  },
  showClearIcon: {
    type: Boolean,
    default: false,
  },
  isSlim: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(['set-location', 'set-selected-location']);

const store = useStore();

const isLocationResultsLoading = ref(false);
const isFocused = ref(false);

const locationSearchRef = ref(null);
useClickAway(locationSearchRef, () => {
  isFocused.value = false;
});

const locationInput = ref(props.inputValue);
watch(
  () => props.inputValue,
  (newVal) => {
    locationInput.value = newVal;
  }
);

const results = computed(() => {
  if (props.type === 'origin') {
    return store.state.laneSearches?.origin?.autocompleteResults;
  } else {
    return store.state.laneSearches?.destination?.autocompleteResults;
  }
});

const autocompleteInput = debounce(async () => {
  const type = props.type;

  if (locationInput.value) {
    isLocationResultsLoading.value = true;
    try {
      await store.dispatch('laneSearches/getAutocomplete', {
        isType: type,
        term: locationInput.value,
      });
    } catch (error) {
      console.error('autocomplete error: ', error);
    } finally {
      isLocationResultsLoading.value = false;
    }
  }
}, 500);

const isItemSelected = ref(false);

const handleAutocompleteResult = async (item) => {
  isItemSelected.value = true;
  const type = props.type;

  await store.dispatch('laneSearches/updateFromGooglePlace', {
    isType: type,
    googlePlace: item,
  });
  switch (type) {
    case 'origin':
      store.commit('laneSearches/updateOrigin', {
        autocompleteResults: [],
        showBadge: true,
      });
      emit('set-location', item?.description);
      break;
    case 'destination':
      store.commit('laneSearches/updateDestination', {
        autocompleteResults: [],
        showBadge: true,
      });
      emit('set-location', item?.description);
      break;
  }

  const areaInformation = await store.dispatch(
    'laneSearches/updateAreaIdFromGooglePlace',
    type
  );
  if (areaInformation?.data) {
    emit('set-selected-location', areaInformation?.data);
  }

  isItemSelected.value = false;
};

const handleClear = () => {
  emit('set-location', '');
  emit('set-selected-location', { id: '', code: '' });

  locationInput.value = '';

  switch (props?.type) {
    case 'origin':
      store.commit('laneSearches/updateOrigin', {
        autocompleteResults: [],
        showBadge: true,
      });
      break;
    case 'destination':
      store.commit('laneSearches/updateDestination', {
        autocompleteResults: [],
        showBadge: true,
      });
      break;
  }
};
</script>
